import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { Award, Download, Eye, Loader2 } from "lucide-react";

const PROGRAMS = [
  {
    key: "rapid",
    name: "R.A.P.I.D.™ Business Recalibration",
    color: "from-purple-600 to-indigo-600",
    accent: "purple",
    description: "Strategic business transformation through Review, Analyze, Plan, Implement & Develop",
    duration: "6-8 weeks"
  },
  {
    key: "podcast",
    name: "Podcast Academy",
    color: "from-pink-600 to-rose-600",
    accent: "pink",
    description: "12-week intensive program to launch and grow your podcast",
    duration: "12 weeks"
  },
  {
    key: "scaling",
    name: "Strategic Scaling Program",
    color: "from-emerald-600 to-teal-600",
    accent: "emerald",
    description: "Comprehensive business scaling with tools, systems, and accountability",
    duration: "90 days"
  }
];

export const CertificatePreview = ({ open, onOpenChange }) => {
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const downloadSample = async (programKey) => {
    setDownloading(true);
    try {
      const res = await authFetch(`${API}/certificates/sample/${programKey}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Sample_${programKey}_Certificate.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Sample certificate downloaded!");
      } else {
        toast.error("Failed to download certificate");
      }
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="certificate-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Program Completion Certificates
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-neutral-500 -mt-2">
          Preview and download sample completion certificates for each program. Clients receive these upon successful program completion.
        </p>

        <div className="space-y-4 mt-2">
          {PROGRAMS.map((program) => (
            <div
              key={program.key}
              className={`rounded-xl border-2 transition-all overflow-hidden cursor-pointer ${
                selectedProgram === program.key
                  ? `border-${program.accent}-300 shadow-md`
                  : "border-neutral-200 hover:border-neutral-300"
              }`}
              onClick={() => setSelectedProgram(selectedProgram === program.key ? null : program.key)}
              data-testid={`cert-program-${program.key}`}
            >
              {/* Program Header */}
              <div className={`bg-gradient-to-r ${program.color} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-base">{program.name}</h3>
                    <p className="text-sm text-white/80">{program.duration}</p>
                  </div>
                  <Award className="w-8 h-8 text-white/60" />
                </div>
              </div>

              {/* Program Details */}
              <div className="p-4 bg-white">
                <p className="text-sm text-neutral-600 mb-3">{program.description}</p>

                {/* Visual Certificate Preview */}
                <div className="border-2 border-dashed border-neutral-200 rounded-lg p-4 bg-neutral-50 mb-3">
                  <div className="text-center space-y-2">
                    <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent" />
                    <p className="text-[10px] uppercase tracking-widest text-neutral-400">Certificate of Completion</p>
                    <p className="text-xs text-neutral-300">This certifies that</p>
                    <p className="text-sm font-semibold text-neutral-700 italic">Your Name Here</p>
                    <p className="text-[10px] text-neutral-400">has successfully completed the</p>
                    <p className="text-xs font-bold text-emerald-600">{program.name}</p>
                    <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent mt-2" />
                    <div className="flex justify-between px-8 pt-2">
                      <div className="text-center">
                        <div className="w-16 h-px bg-neutral-300 mb-1" />
                        <p className="text-[8px] text-neutral-400">Consultant</p>
                      </div>
                      <div className="text-center">
                        <div className="w-16 h-px bg-neutral-300 mb-1" />
                        <p className="text-[8px] text-neutral-400">My Biz Consulting</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => { e.stopPropagation(); downloadSample(program.key); }}
                    disabled={downloading}
                    data-testid={`cert-download-${program.key}`}
                  >
                    {downloading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download Sample PDF
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
